import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import apiHelper from '../../api/ApiHelper';
import { Article, Image, Category } from '../types/ArticleTypes';
import './AddArticle.css'; // Reuse the CSS file

const EditArticle: React.FC = () => {
    const { articleId } = useParams<{ articleId: string }>();
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [isDeleted, setIsDeleted] = useState<boolean>(false);
    const [isEnabled, setIsEnabled] = useState<boolean>(true);
    const [category, setCategory] = useState<Category>(Category.Others);
    const [categoryName, setCategoryName] = useState<string>('');
    const [tags, setTags] = useState<string>('');
    const [images, setImages] = useState<Image[]>([]);

    useEffect(() => {
        fetchArticle();
        fetchImages();
    }, [articleId]);

    const fetchArticle = async () => {
        try {
            const data: Article = await apiHelper.fetchArticle(articleId!);
            setTitle(data.title);
            setDescription(data.description);
            setContent(data.content);
            setIsDeleted(data.isDeleted);
            setIsEnabled(data.isEnabled);
            setCategory(data.category);
            setCategoryName(data.categoryName);

            setTags(data.tags.join(', '));
        } catch (error) {
            alert('Failed to fetch article.');
        }
    };

    const fetchImages = async () => {
        try {
            const data = await apiHelper.fetchArticleImages(articleId!);
            setImages(data.images);
        } catch (error) {
            alert('Failed to fetch images.');
        }
    };

    const formatDate= (date: Date) =>{
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const createdAt = formatDate(new Date());
            await apiHelper.updateArticle({ articleId: articleId ? articleId : "", title, description, content, isDeleted, isEnabled, mainImageUrl: "", category, tags: tags.split(',').map(tag => tag.trim()), createdAt } as Article);
            alert('Post updated successfully!');
        } catch (error) {
            alert('Failed to update post.');
        }
    };

    return (
        <div className="add-article-container">
            <h1>Edit Article</h1>
            <form onSubmit={handleSubmit} className="add-article-form">
                <div className="form-group">
                    <label>Title:</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label>Description:</label>
                    <textarea
                        style={{ height: '50px' }}
                        value={description}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label>Content:</label>
                    <textarea
                        value={content}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label>Category:</label>
                    <select
                        value={categoryName}
                        onChange={(e: ChangeEvent<HTMLSelectElement>) => {setCategory(e.target.value as Category); setCategoryName(e.target.value)}}
                    >
                        <option value="">Select a category</option>
                        {Object.values(Category).map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Tags (comma separated):</label>
                    <input
                        type="text"
                        value={tags}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setTags(e.target.value)}
                    />
                </div>
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={isDeleted}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setIsDeleted(e.target.checked)}
                        />
                        Is Deleted
                    </label>
                </div>
                <div className="form-group">
                    <label>
                        <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setIsEnabled(e.target.checked)}
                        />
                        Is Enabled
                    </label>
                </div>
                <button type="submit">Update Post</button>
            </form>
            <div className="images-list">
                <h2>Images</h2>
                <br />
                {images.map(image => (
                    <div key={image.title} className="image-item">
                        <img src={image.imageUrl} alt={image.title} />
                        <div className="image-details">
                            <p><strong>Type:</strong> {image.type}</p>
                            <p><strong>Title:</strong> {image.title}</p>
                            <p><strong>Content:</strong> {image.content}</p>
                            <p><strong>Source:</strong> {image.source}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default EditArticle;